import {
	BottomSheetFooter,
	type BottomSheetFooterProps,
} from "@gorhom/bottom-sheet";
import { useCallback } from "react";
import { ScrollView } from "react-native";
import { Text } from "react-native-paper";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import type { LevelIconName } from "../../../icon";
import { useAppTheme } from "../../../useAppTheme";
import { useDevice } from "../../../useDevice";
import BottomSheetModalWrapper from "../../BottomSheetModalWrapper";
import Box from "../../Box";
import Button from "../../Button";
import { InsightCallout } from "../../DataCards/InsightCallout";
import DialogWrapper from "../../DialogWrapper";
import { InfoChip } from "../../InfoChip";
import { LevelIcon } from "../../LevelIcon";
export type SessionReportNextActionChip = {
	icon: LevelIconName;
	label?: string;
};

export type SessionReportNextActionDialogSession = {
	icon: LevelIconName;
	title: string;
	subtitle: string;
	chips?: SessionReportNextActionChip[];
};

type Props = {
	visible: boolean;
	onClose: () => void;
	title: string;
	subtitle?: string;
	session: SessionReportNextActionDialogSession;
	insight?: string;
	startLabel: string;
	onStart: () => void;
	starting?: boolean;
};

const DIALOG_MAX_WIDTH = 560;
const SHEET_SNAP_POINT = "70%";
const ICON_CONTAINER_SIZE = 56;

const Header = ({ title, subtitle }: Pick<Props, "title" | "subtitle">) => {
	const theme = useAppTheme();

	return (
		<Box gap={4} alignItems="center" width="100%">
			<Text
				variant="headlineSmall"
				style={{ color: theme.colors.onSurface, textAlign: "center" }}
			>
				{title}
			</Text>
			{subtitle ? (
				<Text
					variant="bodyMedium"
					style={{
						color: theme.colors.onSurfaceVariant,
						textAlign: "center",
					}}
				>
					{subtitle}
				</Text>
			) : null}
		</Box>
	);
};

const SessionSummary = ({
	session,
}: {
	session: SessionReportNextActionDialogSession;
}) => {
	const theme = useAppTheme();
	const { icon, title, subtitle, chips = [] } = session;

	return (
		<Box gap={8} alignItems="center" width="100%">
			<Box
				width={ICON_CONTAINER_SIZE}
				height={ICON_CONTAINER_SIZE}
				borderRadius={ICON_CONTAINER_SIZE / 2}
				alignItems="center"
				justifyContent="center"
				backgroundColor={theme.colors.elevation.level1}
			>
				<LevelIcon name={icon} size={28} color={theme.colors.onSurface} />
			</Box>
			<Text
				variant="titleMedium"
				style={{
					color: theme.colors.onSurface,
					fontWeight: "600",
					textAlign: "center",
				}}
			>
				{title}
			</Text>
			<Text
				variant="bodyMedium"
				style={{ color: theme.colors.onSurfaceVariant, textAlign: "center" }}
			>
				{subtitle}
			</Text>
			{chips.length > 0 ? (
				<Box
					flexDirection="row"
					alignItems="center"
					justifyContent="center"
					gap={8}
					flexWrap="wrap"
					paddingTop={4}
				>
					{chips.map((chip) => (
						<InfoChip
							key={`${chip.icon}-${chip.label}`}
							icon={chip.icon}
							label={chip.label}
						/>
					))}
				</Box>
			) : null}
		</Box>
	);
};

const SessionBar = ({
	insight,
	startLabel,
	onStart,
	starting,
}: Pick<Props, "insight" | "startLabel" | "onStart" | "starting">) => {
	return (
		<Box gap={16} width="100%">
			{insight ? <InsightCallout text={insight} variant="flat" /> : null}
			<Button
				mode="contained"
				icon="play-circle-outline"
				onPress={onStart}
				loading={starting}
				disabled={starting}
			>
				{startLabel}
			</Button>
		</Box>
	);
};

const NextActionSheet = ({
	visible,
	onClose,
	title,
	subtitle,
	session,
	insight,
	startLabel,
	onStart,
	starting,
}: Props) => {
	const theme = useAppTheme();
	const insets = useSafeAreaInsets();

	const renderFooter = useCallback(
		(footerProps: BottomSheetFooterProps) => (
			<BottomSheetFooter {...footerProps}>
				<Box
					paddingHorizontal={16}
					paddingTop={16}
					paddingBottom={insets.bottom + 16}
					borderTopWidth={1}
					borderTopColor={theme.colors.outlineVariant}
					backgroundColor={theme.colors.surface}
				>
					<SessionBar
						insight={insight}
						startLabel={startLabel}
						onStart={onStart}
						starting={starting}
					/>
				</Box>
			</BottomSheetFooter>
		),
		[
			insight,
			onStart,
			startLabel,
			starting,
			theme.colors.outlineVariant,
			theme.colors.surface,
			insets.bottom,
		],
	);

	return (
		<BottomSheetModalWrapper
			showModal={visible}
			snapIndex={0}
			snapPoint={SHEET_SNAP_POINT}
			animateOnMount
			enablePanDownToClose
			enableDismissOnClose
			onDismiss={onClose}
			backgroundStyle={{
				backgroundColor: theme.colors.surface,
			}}
			footerComponent={renderFooter}
			contentContainerStyle={{
				gap: 16,
			}}
		>
			<Header title={title} subtitle={subtitle} />
			<SessionSummary session={session} />
		</BottomSheetModalWrapper>
	);
};

export const SessionReportNextActionDialog = (props: Props) => {
	const theme = useAppTheme();
	const { isMobile } = useDevice();

	if (isMobile) {
		return <NextActionSheet {...props} />;
	}

	const {
		visible,
		onClose,
		title,
		subtitle,
		session,
		insight,
		startLabel,
		onStart,
		starting,
	} = props;

	return (
		<DialogWrapper
			visible={visible}
			handleClose={onClose}
			showCloseButton
			maxWidth={DIALOG_MAX_WIDTH}
			width="100%"
		>
			<ScrollView
				showsVerticalScrollIndicator={false}
				contentContainerStyle={{
					paddingTop: 16,
					paddingBottom: 16,
					paddingHorizontal: 24,
					gap: 16,
				}}
			>
				<Header title={title} subtitle={subtitle} />
				<SessionSummary session={session} />
			</ScrollView>
			<Box
				borderTopWidth={1}
				borderColor={theme.colors.outlineVariant}
				paddingHorizontal={24}
				paddingTop={16}
				paddingBottom={16}
			>
				<SessionBar
					insight={insight}
					startLabel={startLabel}
					onStart={onStart}
					starting={starting}
				/>
			</Box>
		</DialogWrapper>
	);
};
